/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CARD } from '../theme';

/**
 * Carte de projet.
 *
 * Deux formats sur une meme structure : `featured` pour la realisation mise
 * en avant, compact pour les suivantes. Faute de photographies, le titre du
 * projet fait office de visuel, sur un bandeau sombre.
 *
 * `fullName` developpe les acronymes maison (CAMEDAF, FaDel, ConnectBiz,
 * FinSight) : sans cela, un visiteur exterieur lit une suite de sigles opaques.
 */
export const ProjectCard = ({ project, language, viewLabel, featured = false }) => {
  const fullName = project.fullName ? project.fullName[language] : null;

  return (
    <Link
      to="/tchad/projects"
      data-testid={`tchad-project-${project.id}`}
      className={`${CARD} group flex h-full flex-col overflow-hidden ${
        featured ? 'lg:flex-row' : ''
      }`}
    >
      {/* Carte phare : au-dela de `lg`, le bloc sombre passe debout, en colonne
          etroite a gauche. Empile en bandeau, il devait absorber toute la
          hauteur des deux cartes voisines et formait une dalle noire
          disproportionnee ; dressee, la meme surface se lit comme une colonne.
          En dessous de `lg`, la carte reste empilee et le bloc reprend sa
          hauteur naturelle. */}
      <div
        className={`relative bg-slate-950 text-white ${
          featured
            ? 'flex flex-col px-10 py-12 lg:w-[34%] lg:shrink-0'
            : 'px-8 py-8'
        }`}
      >
        <div className="absolute inset-0 bg-tchad-weave" />
        <div className={`relative ${featured ? 'flex flex-1 flex-col' : ''}`}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            <span>{project.country}</span>
            <span className="h-1 w-1 rounded-full bg-amber-400" />
            <span>{project.year}</span>
          </div>

          <h3
            className={`mt-4 font-bold leading-snug tracking-tight text-white ${
              featured ? 'text-3xl lg:text-4xl' : 'text-xl'
            }`}
          >
            {project.title[language]}
          </h3>

          {fullName ? (
            <p className="no-justify mt-2 text-sm font-medium text-amber-400/90">{fullName}</p>
          ) : null}

          <span
            className={`inline-flex w-fit items-center rounded-sm border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-amber-400 ${
              featured ? 'mt-auto pt-1' : 'mt-6'
            }`}
          >
            {project.impact[language]}
          </span>
        </div>
      </div>

      <div className={`flex flex-1 flex-col ${featured ? 'p-10' : 'p-8'}`}>
        <span className="no-justify text-xs font-semibold uppercase tracking-widest text-slate-500">
          {project.category}
        </span>
        <p
          className={`mt-3 flex-1 leading-relaxed text-slate-600 ${
            featured ? 'text-base' : 'text-sm line-clamp-4'
          }`}
        >
          {project.description[language]}
        </p>

        {Array.isArray(project.technologies) && project.technologies.length ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {(featured ? project.technologies : project.technologies.slice(0, 3)).map((tech) => (
              <span
                key={tech}
                className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600"
              >
                {tech}
              </span>
            ))}
          </div>
        ) : null}

        <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-950">
          {viewLabel}
          <ArrowRight className="h-3.5 w-3.5 rtl-flip transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};
